/* eslint-disable import/prefer-default-export */
import React from 'react';
import {
  Badge,
  BadgeProps,
  Avatar,
  AvatarGroup,
  SxProps,
  Slide,
  Box,
  Chip,
} from '@mui/material';
import {
  Groups as GroupsIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import moment from 'moment';

import { getShortName } from '../../../helper';
import { IConversation, IConversationUser } from '../../../store/V2/ChatSlice';

export const getConversationName = (
  conversation: IConversation,
  userId?: string
): string => {
  const users = conversation.users as IConversationUser[];

  if (users.length === 0) return 'Everyone';

  const otherUsers = users.filter(({ _id }) => _id !== userId);

  if (otherUsers.length === 1) {
    const { firstname, lastname = '' } = otherUsers[0];
    return `${firstname} ${lastname}`.trim();
  }

  return otherUsers
    .map(({ firstname, lastname }) => getShortName(firstname, lastname))
    .join(', ');
};

export const getConversationAvatar = (
  conversation: IConversation,
  userId?: string
): JSX.Element => {
  const users =
    conversation && conversation.users
      ? (conversation.users as IConversationUser[])
      : [];

  const list = users.filter(({ _id }) => _id !== userId);

  const badgeProps: BadgeProps = {
    overlap: 'circular',
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    variant: 'dot',
    sx: {
      '& .MuiBadge-badge': {
        boxShadow: '0 0 0 2px #fff',
      },
    },
  };

  const avatarStyles: SxProps = {
    width: '35px',
    height: '35px',
  };

  if (list.length <= 2 && list.length > 0) {
    if (list.length === 1) {
      const userData = list[0];
      const { isOnline, avatar, firstname } = userData;

      return (
        <Badge {...badgeProps} color={isOnline ? 'success' : 'warning'}>
          <Avatar
            alt={firstname}
            src={avatar}
            sx={{ ...avatarStyles, pl: '-5px' }}
          />
        </Badge>
      );
    }

    return (
      <AvatarGroup max={2}>
        {list.map((userData, index) => {
          const { isOnline, avatar, firstname } = userData;

          return (
            <Badge
              key={`group-avatar-${index}`}
              {...badgeProps}
              color={isOnline ? 'success' : 'warning'}
            >
              <Avatar alt={firstname} src={avatar} sx={avatarStyles} />
            </Badge>
          );
        })}
      </AvatarGroup>
    );
  }

  return (
    <Badge {...badgeProps} invisible>
      <Avatar sx={avatarStyles}>
        <GroupsIcon />
      </Avatar>
    </Badge>
  );
};

const formatLastSeen = (lastSeen: Date | string) => {
  if (!lastSeen) return 'Never';

  const now = moment();
  const lastSeenMoment = moment(lastSeen);
  const diffMinutes = now.diff(lastSeenMoment, 'minutes');

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = now.diff(lastSeenMoment, 'hours');
  if (diffHours < 24) return `${diffHours} hr ago`;

  const diffDays = now.diff(lastSeenMoment, 'days');
  if (diffDays < 7) return `${diffDays} days ago`;

  return lastSeenMoment.format('MMM D, YYYY');
};

export const getDetailedLastSeen = (
  consversation: IConversation,
  authUserId: string
) => {
  const users = consversation.users as IConversationUser[];

  if (users.length === 2) {
    const [userData] = users.filter(({ _id }) => _id !== authUserId);

    if (userData) {
      const { isOnline, lastSeen } = userData;

      if (isOnline) {
        return 'online';
      }

      if (lastSeen) {
        return `last seen ${formatLastSeen(lastSeen)}`;
      }
    }
  }

  return '';
};

export const UnreadMessageElement = () => {
  return (
    <Slide in key="unread-element-slide">
      <Box
        key="unread-element"
        id="unread-element"
        sx={{ display: 'flex', justifyContent: 'center' }}
      >
        <Chip
          color="info"
          icon={<InfoOutlinedIcon />}
          label="Unread Messages"
        />
      </Box>
    </Slide>
  );
};

export const formatMessageTime = (timestamp: string | Date) => {
  const now = moment();
  const messageTime = moment(timestamp);

  if (messageTime.isSame(now, 'day')) {
    return messageTime.format('h:mm A'); // Today
  }
  if (messageTime.isSame(now.subtract(1, 'day'), 'day')) {
    return 'Yesterday'; // Yesterday
  }
  return messageTime.format('MMM D'); // Older dates
};
